import { alert } from "@components/common/Alert";
import MultiEmailInput from "@components/common/inputs/MultiEmailInput";
import { DSButton } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms";
import {
  faCopy,
  faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTemplateSharing, useTeam, useTemplates } from "@hooks";
import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";

import { SCRIBENOTE_WEB } from "../../../constants";
import { copyToClipboard } from "../../../utils";
import { trackOperation } from "../../../utils/operationTracking";
import { SHARE_OPTIONS } from "../constants";

// Unmount content when hidden so state resets on each open
const ShareTemplateModal = ({ shouldShow, template, ...props }) => {
  if (!shouldShow || !template?.uuid) return null;
  return (
    <ShareTemplateModalContent
      key={template.uuid}
      template={template}
      {...props}
    />
  );
};

const ShareTemplateModalContent = ({
  hideModal,
  template,
  onShare,
  refetch,
  customTitle,
  source,
}) => {
  const [selectedShareOption, setSelectedShareOption] = useState(
    () => template.sharingType || "",
  );
  const [sharedEmails, setSharedEmails] = useState(
    () => template.sharedEmails || [],
  );
  const [initialEmails, setInitialEmails] = useState(
    () => template.sharedEmails || [],
  );
  const [initialShareOption, setInitialShareOption] = useState(
    () => template.sharingType || "",
  );
  const { shareTemplate } = useTemplateSharing();
  const { userTeams } = useTeam();
  const {
    defaultTemplatesForSelectedOrg,
    refetchTeamDefaults,
    refetchOrgDefaults,
  } = useTemplates();

  const availableShareOptions =
    !userTeams || userTeams.length === 0
      ? SHARE_OPTIONS.filter((option) => option.value !== "TEAM")
      : SHARE_OPTIONS;

  const hasTeamDefaults =
    template?.teamDefaults && template.teamDefaults.length > 0;
  const isOrgDefault = defaultTemplatesForSelectedOrg?.some(
    (t) => t.uuid === template?.uuid,
  );
  const isTemplateADefault = hasTeamDefaults || isOrgDefault;

  useEffect(() => {
    if (source) {
      mixpanel.track("Template Sharing Modal Opened", {
        templateUuid: template.uuid,
        source: source,
        sharingType: template.sharingType,
      });
    }
  }, [template.uuid, source, template.sharingType]);

  const hasUnsavedEmailChanges =
    JSON.stringify([...sharedEmails].sort()) !==
    JSON.stringify([...initialEmails].sort());

  const hasUnsavedShareOption =
    selectedShareOption !== initialShareOption;

  const handleShareOptionChange = (newShareOption) => {
    setSelectedShareOption(newShareOption);
  };

  const handleSaveEmails = () =>
    trackOperation("template-sharing-emails-update", async (ctx) => {
      if (!template?.uuid || !initialShareOption) return;

      ctx.addProperties({
        templateUuid: template.uuid,
        previousListOfEmails: initialEmails,
        newListOfEmails: sharedEmails,
      });

      await shareTemplate(template, initialShareOption, sharedEmails);

      alert("success", "Shared emails updated successfully!");

      setInitialEmails(sharedEmails);
    });

  const handleCancel = () => {
    setSelectedShareOption("");
    setSharedEmails([]);
    setInitialEmails([]);
    setInitialShareOption("");
    hideModal();
  };

  const handleDone = async () => {
    if (hasUnsavedShareOption || hasUnsavedEmailChanges) {
      let saveSucceeded = false;
      await trackOperation("template-sharing-update", async (ctx) => {
        if (!template?.uuid) return;

        ctx.addProperties({
          templateUuid: template.uuid,
          newSharingType: selectedShareOption,
          previousSharingType: initialShareOption,
          emails: sharedEmails,
        });

        await shareTemplate(
          template,
          selectedShareOption,
          sharedEmails,
        );

        saveSucceeded = true;
        alert("success", "Sharing settings updated!");
      });

      if (!saveSucceeded) return;
    }

    if (refetch) {
      await refetch();
    }
    if (refetchTeamDefaults) {
      await refetchTeamDefaults();
    }
    if (refetchOrgDefaults) {
      await refetchOrgDefaults();
    }
    if (onShare) {
      onShare();
    }
    setSelectedShareOption("");
    setSharedEmails([]);
    setInitialEmails([]);
    setInitialShareOption("");
    hideModal();
  };

  const handleCopyLink = (e) =>
    trackOperation("template-share-link-copied", async (ctx) => {
      e.stopPropagation();

      ctx.addProperties({
        templateUuid: template?.uuid,
        source: "public share option",
      });

      const linkToCopy = `${SCRIBENOTE_WEB}/templates/shared/${template?.uuid}`;
      await copyToClipboard(linkToCopy);
    });

  const handleCopyLinkButton = () =>
    trackOperation("template-share-link-copied", async (ctx) => {
      if (!template?.uuid) return;

      ctx.addProperties({
        templateUuid: template.uuid,
        source: "template sharing modal footer",
      });

      const linkToCopy = `${SCRIBENOTE_WEB}/templates/shared/${template.uuid}`;
      await copyToClipboard(linkToCopy);
    });

  const modalTitle =
    customTitle ||
    `Choose how to share ${template?.name || "this template"}`;

  const hasUnsavedChanges =
    hasUnsavedShareOption || hasUnsavedEmailChanges;
  const confirmButtonText = hasUnsavedChanges
    ? "Save & Close"
    : "Done";

  return (
    <DSModal
      isOpen
      onClose={handleCancel}
      title={modalTitle}
      actions={[
        {
          label: "Copy Link",
          variant: "cancel",
          onClick: handleCopyLinkButton,
        },
        {
          label: confirmButtonText,
          variant: "primary",
          onClick: handleDone,
          dataTestId: "confirm-button",
        },
      ]}
      dataTestId="share-template-modal"
    >
      <h4 className="mb-sm">Share with Specific People (Optional)</h4>
      <MultiEmailInput
        emails={sharedEmails || []}
        setEmails={setSharedEmails}
        placeholder="Add email addresses"
        infoText="Add email addresses to share with specific people in addition to the general access level."
        dataTestId="template-share-emails"
      />
      {hasUnsavedEmailChanges && (
        <div className="flex flex-row items-center justify-between">
          <p className="font-medium text-sm text-danger mr-4">
            You have unsaved changes to shared emails. New emails will
            receive an email notification when you save the email
            changes.
          </p>
          <DSButton
            onClick={handleSaveEmails}
            size="sm"
            dataTestId="save-email-changes-button"
          >
            Save Email Changes
          </DSButton>
        </div>
      )}
      <h4 className="mb-sm mt-6">General Access</h4>
      <div className="space-y-1">
        {availableShareOptions.map((option) => (
          <div
            key={option.value}
            onClick={() => handleShareOptionChange(option.value)}
            className={`flex-1 ${
              selectedShareOption === option.value &&
              "bg-brand-subtle"
            } hover:bg-secondary rounded-large p-sm cursor-pointer transition-colors`}
            data-testid={`share-template-option-${option.value}`}
          >
            <div className="flex flex-row items-center space-x-3">
              <input
                onChange={() => handleShareOptionChange(option.value)}
                checked={selectedShareOption === option.value}
                type="radio"
                value={option.value}
                className="text-brand focus:ring-0"
              />
              <FontAwesomeIcon
                icon={option.icon}
                className="text-brand"
                size="lg"
              />
              <div className="flex flex-col flex-1">
                <h5
                  className={`font-semibold cursor-pointer ${
                    selectedShareOption === option.value &&
                    "text-brand"
                  }`}
                >
                  {option.title}
                </h5>
                <h5 className="text-xs text-secondary">
                  {option.description}
                </h5>
              </div>
              {option.value === "TEAM" &&
                selectedShareOption === "TEAM" &&
                isTemplateADefault && (
                  <>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-brand text-xs"
                    />
                    <span className="text-xs text-brand font-medium">
                      Team Default - changing sharing options will
                      remove it as a default
                    </span>
                  </>
                )}
              {option.value === "PUBLIC" && (
                <DSButton
                  onClick={handleCopyLink}
                  size="sm"
                  icon={faCopy}
                  dataTestId="copy-share-link-button"
                >
                  Copy Link
                </DSButton>
              )}
            </div>
          </div>
        ))}
      </div>
    </DSModal>
  );
};

export default ShareTemplateModal;
